import { useState } from 'react'
import { Search, Bell, User, LogOut, Settings, LogIn, UserPlus, Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/stores/auth-store'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

export function TopNavigation() {
  const { user, logout, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const handleLogout = () => {
    logout()
    toast.success('已退出登录')
    navigate('/')
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`)
    }
  }

  const handleNotificationClick = () => {
    if (isAuthenticated) {
      navigate('/messages')
    } else {
      toast.info('请先登录查看消息通知')
      navigate('/login')
    }
  }

  const handleProfileClick = () => {
    if (isAuthenticated) {
      navigate('/profile')
    } else {
      toast.info('请先登录访问个人中心')
      navigate('/login')
    }
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-200/50 shadow-sm">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between max-w-7xl">
        {/* Logo */}
        <div className="flex items-center space-x-3">
          <div 
            className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          >
            <div className="w-9 h-9 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-base">悟</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-xl text-gray-900">大悟万能墙</span>
              <div className="text-xs text-gray-500 -mt-1">Campus Wall</div>
            </div>
          </div>
        </div>

        {/* 搜索框 - 桌面端 */}
        <div className="hidden md:flex flex-1 max-w-lg mx-6">
          <form onSubmit={handleSearch} className="relative w-full">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input 
              placeholder="搜索校园动态、用户、话题..." 
              className="pl-12 pr-4 py-2.5 bg-gray-50/80 border-0 focus:bg-white focus:ring-2 focus:ring-primary/20 rounded-full transition-all duration-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </form>
        </div>

        {/* 移动端搜索按钮 */}
        <Button 
          variant="ghost" 
          size="sm" 
          className="md:hidden"
          onClick={() => navigate('/search')}
        >
          <Search className="w-5 h-5" />
        </Button>

        {/* 右侧操作 */}
        <div className="flex items-center space-x-2">
          {/* 消息通知 - 游客点击会提示登录 */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="relative hover:bg-gray-100 rounded-full p-2"
            onClick={handleNotificationClick}
          >
            <Bell className="w-5 h-5" />
          </Button>
          
          {/* 用户菜单 */}
          {isAuthenticated ? (
            // 已登录用户菜单
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full hover:ring-2 hover:ring-primary/20 transition-all">
                  <Avatar className="h-9 w-9 border-2 border-white shadow-md">
                    <AvatarImage src={user?.avatar} alt={user?.username} />
                    <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-white font-semibold">
                      {user?.username?.[0]?.toUpperCase() || <User className="w-4 h-4" />}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64" align="end" forceMount>
                <div className="flex items-center justify-start gap-3 p-4 bg-gradient-to-r from-primary/5 to-primary/10">
                  <Avatar className="h-12 w-12 border-2 border-white shadow-sm">
                    <AvatarImage src={user?.avatar} alt={user?.username} />
                    <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-white font-semibold">
                      {user?.username?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col space-y-1 leading-none">
                    <p className="font-semibold text-gray-900">{user?.username}</p>
                    <p className="text-sm text-muted-foreground truncate max-w-[160px]">
                      {user?.email}
                    </p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleProfileClick} className="py-3">
                  <User className="mr-3 h-4 w-4 text-primary" />
                  <span>个人中心</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="py-3 text-destructive focus:text-destructive">
                  <LogOut className="mr-3 h-4 w-4" />
                  <span>退出登录</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            // 游客登录按钮
            <div className="flex items-center space-x-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/login')}
                className="hidden md:flex hover:bg-primary/10 text-primary font-medium"
              >
                <LogIn className="w-4 h-4 mr-2" />
                登录
              </Button>
              <Button 
                size="sm"
                onClick={() => navigate('/login?tab=register')}
                className="hidden md:flex bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-md"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                注册
              </Button>
              
              {/* 移动端用户图标 */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="md:hidden relative h-10 w-10 rounded-full hover:bg-gray-100">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-gray-100">
                        <User className="w-4 h-4 text-gray-600" />
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64" align="end" forceMount>
                  <div className="flex items-center justify-start gap-3 p-4 bg-gradient-to-r from-gray-50 to-gray-100">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-gray-200">
                        <User className="w-6 h-6 text-gray-500" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-semibold text-gray-900">游客模式</p>
                      <p className="text-sm text-muted-foreground">
                        登录后享受更多功能
                      </p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/login')} className="py-3">
                    <LogIn className="mr-3 h-4 w-4 text-primary" />
                    <span>登录</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/login?tab=register')} className="py-3">
                    <UserPlus className="mr-3 h-4 w-4 text-primary" />
                    <span>注册</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}